import React, { useState } from 'react'
import Box from '@mui/joy/Box'
import IconButton from '@mui/joy/IconButton'
import Stack from '@mui/joy/Stack'
import Sheet from '@mui/joy/Sheet'
import Typography from '@mui/joy/Typography'
import Tooltip from '@mui/joy/Tooltip'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import DownloadIcon from '@mui/icons-material/Download'
import Divider from '@mui/joy/Divider/index.js'
import { post } from 'aws-amplify/api'
import { fetchAuthSession } from 'aws-amplify/auth'

// Helper function to safely access nested properties
const safelyGetProp = (obj, path, defaultValue = undefined) =>
  path
    .split('.')
    .reduce(
      (prev, curr) =>
        prev && prev[curr] !== undefined ? prev[curr] : defaultValue,
      obj,
    )

export default function ChatBubble({
  referenceKey,
  isYou,
  cacheTitle,
  metadata,
  contextObj,
  contents, // New prop for multiple contents
}) {
  const [isExpanded, toggleExpanded] = useState(false)
  const [isDownloading, setIsDownloading] = useState(false)

  const handleDownload = async () => {
    try {
      setIsDownloading(true)

      // Get the S3 URI from metadata
      const s3Uri = metadata?.['x-amz-bedrock-kb-source-uri']

      if (!s3Uri) {
        console.error('No S3 URI found in metadata')
        alert('Unable to download: Source document URI not found')
        return
      }

      // Get auth session for the API call
      const session = await fetchAuthSession()
      const token = session.tokens?.idToken?.toString()

      if (!token) {
        console.error('No auth token found')
        alert('Unable to download: Authentication required')
        return
      }

      // Call the API to get presigned URL
      const restOperation = post({
        apiName: 'technician-bot',
        path: '/1/citation/download-url',
        options: {
          body: { s3Uri },
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      })

      const { body } = await restOperation.response
      const response = await body.json()

      if (response.presignedUrl) {
        // Open the presigned URL in a new tab to trigger download
        window.open(response.presignedUrl, '_blank')
      } else {
        throw new Error('No presigned URL in response')
      }
    } catch (error) {
      console.error('Error downloading PDF:', error)
      alert('Failed to download PDF. Please try again.')
    } finally {
      setIsDownloading(false)
    }
  }

  return (
    <Stack
      direction="column"
      spacing={1}
      sx={{ width: '100%', maxWidth: '600px', mb: 1 }}
    >
      <Stack
        direction="row"
        spacing={2}
        flexDirection={isYou ? 'row-reverse' : 'row'}
        alignItems="center"
      >
        <Box sx={{ flexGrow: 1 }}>
          <Typography level="body-sm" sx={{ fontWeight: 'bold' }}>
            {cacheTitle}
          </Typography>
          {metadata?.FileSize && (
            <Typography level="body-xs" sx={{ color: 'text.secondary' }}>
              {`${(parseInt(metadata.FileSize) / 1024 / 1024).toFixed(2)}MB`}
            </Typography>
          )}
        </Box>
        <Tooltip title="Download PDF" variant="soft">
          <IconButton
            size="sm"
            variant="soft"
            color="primary"
            onClick={() => {
              void handleDownload()
            }}
            loading={isDownloading}
            disabled={!metadata?.['x-amz-bedrock-kb-source-uri']}
          >
            <DownloadIcon />
          </IconButton>
        </Tooltip>
        <IconButton
          size="sm"
          variant="plain"
          color="neutral"
          onClick={() => {
            toggleExpanded(!isExpanded)
          }}
        >
          {isExpanded ?
            <ExpandLessIcon />
          : <ExpandMoreIcon />}
        </IconButton>
      </Stack>

      {isExpanded && (
        <Sheet
          variant="outlined"
          sx={{
            p: 2,
            ml: isYou ? 0 : 2,
            mr: isYou ? 2 : 0,
            backgroundColor: 'background.surface',
            borderRadius: 'sm',
            fontSize: '0.875rem',
          }}
        >
          <Typography
            component="h3"
            sx={{
              fontWeight: 'bold',
              fontSize: '0.875rem',
              color: 'primary.500',
              mb: 1,
            }}
          >
            Document Details
          </Typography>

          <Stack spacing={1}>
            <Stack direction="row" justifyContent="space-between">
              <Typography
                sx={{
                  fontSize: '0.75rem',
                  color: 'text.secondary',
                }}
              >
                Title:
              </Typography>
              <Typography sx={{ fontSize: '0.875rem' }}>
                {cacheTitle}
              </Typography>
            </Stack>

            <Stack direction="row" justifyContent="space-between">
              <Typography
                sx={{
                  fontSize: '0.75rem',
                  color: 'text.secondary',
                }}
              >
                Date:
              </Typography>
              <Typography sx={{ fontSize: '0.875rem' }}>
                {safelyGetProp(metadata, 'DatePublished', 'N/A')}
              </Typography>
            </Stack>

            <Stack direction="row" justifyContent="space-between">
              <Typography
                sx={{
                  fontSize: '0.75rem',
                  color: 'text.secondary',
                }}
              >
                File Location:
              </Typography>
              <Typography sx={{ fontSize: '0.875rem' }}>
                {safelyGetProp(metadata, 'FileLocation', 'N/A')}
              </Typography>
            </Stack>

            <Stack direction="row" justifyContent="space-between">
              <Typography
                sx={{
                  fontSize: '0.75rem',
                  color: 'text.secondary',
                }}
              >
                Model:
              </Typography>
              <Typography sx={{ fontSize: '0.875rem' }}>
                {safelyGetProp(metadata, 'Model', 'N/A')}
              </Typography>
            </Stack>

            <Stack direction="row" justifyContent="space-between">
              <Typography
                sx={{
                  fontSize: '0.75rem',
                  color: 'text.secondary',
                }}
              >
                Object Id:
              </Typography>
              <Typography sx={{ fontSize: '0.875rem' }}>
                {safelyGetProp(metadata, 'ObjectId', 'N/A')}
              </Typography>
            </Stack>

            {metadata?.ContentType && (
              <Stack direction="row" justifyContent="space-between">
                <Typography
                  sx={{
                    fontSize: '0.75rem',
                    color: 'text.secondary',
                  }}
                >
                  Content Type:
                </Typography>
                <Typography sx={{ fontSize: '0.875rem' }}>
                  {metadata.ContentType.toUpperCase()}
                </Typography>
              </Stack>
            )}

            {metadata?.LitTypeDescription && (
              <Stack direction="row" justifyContent="space-between">
                <Typography
                  sx={{
                    fontSize: '0.75rem',
                    color: 'text.secondary',
                  }}
                >
                  Document Type:
                </Typography>
                <Typography sx={{ fontSize: '0.875rem' }}>
                  {metadata.LitTypeDescription}
                </Typography>
              </Stack>
            )}

            {metadata?.LitOrderNbr && (
              <Stack direction="row" justifyContent="space-between">
                <Typography
                  sx={{
                    fontSize: '0.75rem',
                    color: 'text.secondary',
                  }}
                >
                  Order Number:
                </Typography>
                <Typography sx={{ fontSize: '0.875rem' }}>
                  {metadata.LitOrderNbr}
                </Typography>
              </Stack>
            )}

            {metadata?.LitLanguage && (
              <Stack direction="row" justifyContent="space-between">
                <Typography
                  sx={{
                    fontSize: '0.75rem',
                    color: 'text.secondary',
                  }}
                >
                  Language:
                </Typography>
                <Typography sx={{ fontSize: '0.875rem' }}>
                  {metadata.LitLanguage.toUpperCase()}
                </Typography>
              </Stack>
            )}

            <Divider />

            <Stack direction="row" justifyContent="space-between">
              <Typography
                sx={{
                  fontSize: '0.75rem',
                  color: 'text.secondary',
                }}
              >
                Content
                {contents && contents.length > 1 ?
                  ` (${contents.length} excerpts)`
                : ''}
                :
              </Typography>
            </Stack>
            <Box
              sx={{
                maxHeight: '300px',
                overflow: 'auto',
                backgroundColor: 'background.level2',
                p: 1,
                borderRadius: 'sm',
              }}
            >
              {contents && contents.length > 0 ?
                contents.map((content, index) => (
                  <Box key={index}>
                    {index > 0 && <Divider sx={{ my: 1 }} />}
                    <Typography
                      sx={{
                        fontSize: '0.875rem',
                        fontFamily: 'monospace',
                        whiteSpace: 'pre-wrap',
                      }}
                    >
                      {safelyGetProp(content, 'text', 'No content available')}
                    </Typography>
                  </Box>
                ))
              : <Typography
                  sx={{
                    fontSize: '0.875rem',
                    fontFamily: 'monospace',
                    whiteSpace: 'pre-wrap',
                  }}
                >
                  {safelyGetProp(contextObj, 'text', 'No content available')}
                </Typography>
              }
            </Box>
          </Stack>
        </Sheet>
      )}
    </Stack>
  )
}
